import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Collapse, Popconfirm, Switch, Typography } from 'antd';
import moment from 'moment';
import React from 'react';
import { apiClient } from '@api/client';
import { nonNull } from '@helpers/non-null';
import {
  figmaAlert,
  figmaPostFailureAdminText,
  figmaSettingsQuery
} from '@helpers/figmaPosting';
import UserAvatar from '@components/UserAvatar';
import { IntegrationsFigmaIcon } from '@icons';
import type { FigmaConnectionDto, FigmaSettingsDto } from '@api/Api';
import type { InstalledIntegrationDetailsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import {
  ConfirmText,
  FigmaPluginSteps,
  figmaAccountLabel,
  PostingAccountRow
} from '@pages/AdminSettings/components/IntegrationTab/figma-shared';
import './figma.less';

const { Title, Text } = Typography;

/** Comments that couldn't be posted lately, why, and sending them again. */
function FailedPosts({
  integrationId,
  settings,
  onSent
}: {
  integrationId: string;
  settings: FigmaSettingsDto;
  onSent: () => void;
}) {
  const sendAgain = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.figma.figmaControllerRetryCommentPosts({
        integrationId
      });
      return data.queued;
    },
    // They're posted from the queue, a few seconds later.
    onSuccess: () => setTimeout(onSent, 5000)
  });
  const alert = figmaAlert({ ...settings, postingAccountLost: false });
  if (!alert) return null;
  return (
    <Alert
      type="warning"
      showIcon
      message={alert.title}
      description={
        <>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {settings.failedPosts.map((x) => (
              <li key={x.reason}>
                {figmaPostFailureAdminText(
                  x.reason,
                  x.count,
                  !!settings.postingAccount
                )}
              </li>
            ))}
          </ul>
          {sendAgain.isSuccess && (
            <Text type="secondary">
              {sendAgain.data
                ? `Sending ${sendAgain.data} ${
                    sendAgain.data === 1 ? 'comment' : 'comments'
                  } again. Those that still can't be posted stay listed here.`
                : 'Nothing to send again.'}
            </Text>
          )}
          {sendAgain.isError && (
            <Text type="danger">
              Couldn&apos;t send them again. Check your connection and try
              again.
            </Text>
          )}
        </>
      }
      action={
        <Button
          size="small"
          loading={sendAgain.isLoading}
          disabled={sendAgain.isSuccess}
          onClick={() => sendAgain.mutate()}
        >
          Send again
        </Button>
      }
    />
  );
}

/** Who connected Figma in this organization; admins can revoke each one. */
export default function FigmaInstalledDetails({
  integration
}: InstalledIntegrationDetailsProps) {
  const { data, refetch } = useQuery({
    suspense: true,
    queryKey: ['figma', 'connections', integration.id],
    queryFn: async () => {
      const { data } = await apiClient.figma.figmaControllerListConnections({
        integrationId: integration.id
      });
      return data.list;
    }
  });
  const connections = nonNull(data);

  const settings = useQuery({
    ...figmaSettingsQuery(integration.id),
    suspense: true
  });
  const updateSettings = useMutation({
    mutationFn: async (postComments: boolean) => {
      await apiClient.figma.figmaControllerUpdateSettings({
        integrationId: integration.id,
        postComments
      });
    },
    onSettled: () => settings.refetch()
  });
  const postComments = updateSettings.isLoading
    ? !!updateSettings.variables
    : !!settings.data?.postComments;

  const revoke = useMutation({
    mutationFn: async (connectionId: string) => {
      await apiClient.figma.figmaControllerRevokeConnection({
        integrationId: integration.id,
        connectionId
      });
    },
    onSettled: () => refetch()
  });

  return (
    <div className="figma_details">
      <div className="figma_details-header">
        <IntegrationsFigmaIcon width={48} height={48} />
        <div>
          <Title level={3}>Figma</Title>
          <Text type="secondary">
            Members connect their own Figma account in the StreamWork plugin.
          </Text>
        </div>
      </div>

      <section className="figma_details-section">
        <Title level={5} className="figma_details-section-title">
          Comments
        </Title>
        {settings.data && postComments && (
          <FailedPosts
            integrationId={integration.id}
            settings={settings.data}
            onSent={() => settings.refetch()}
          />
        )}
        <div className="figma_details-card">
          <div className="figma_details-row">
            <div className="figma_details-row-text">
              <Text strong id="figma-post-comments">
                Post comments to Figma
              </Text>
              <Text type="secondary">
                New comments on media exported from Figma, and replies to Figma
                threads, show up in the Figma file with a link back to
                StreamWork.
              </Text>
              {updateSettings.isError && (
                <Text type="danger">
                  Couldn&apos;t save the setting. Check your connection and try
                  again.
                </Text>
              )}
            </div>
            <div className="figma_details-row-control">
              <Switch
                aria-labelledby="figma-post-comments"
                checked={postComments}
                loading={updateSettings.isLoading}
                onChange={(checked) => updateSettings.mutate(checked)}
              />
            </div>
          </div>
          {settings.data && postComments && (
            <PostingAccountRow
              integrationId={integration.id}
              settings={settings.data}
              onChange={() => settings.refetch()}
            />
          )}
        </div>
      </section>

      <section className="figma_details-section">
        <Title level={5} className="figma_details-section-title">
          Plugin
        </Title>
        <div className="figma_details-card">
          <div className="figma_details-row">
            <div className="figma_details-row-text">
              <Text strong>StreamWork plugin for Figma</Text>
              <Text type="secondary">
                Designers install it in Figma, then connect their own Figma
                account to send designs, slides, boards and assets to
                StreamWork.
              </Text>
            </div>
          </div>
          <Collapse ghost>
            <Collapse.Panel header="How to install it" key="steps">
              <FigmaPluginSteps />
            </Collapse.Panel>
          </Collapse>
        </div>
      </section>

      <section className="figma_details-section">
        <Title level={5} className="figma_details-section-title">
          Connected members{' '}
          <Text type="secondary" style={{ fontWeight: 'normal' }}>
            {connections.length}
          </Text>
        </Title>
        <div className="figma_details-card">
          {connections.length === 0 && (
            <Text type="secondary" className="figma_details-empty">
              Nobody has connected Figma yet.
            </Text>
          )}
          {connections.map((connection: FigmaConnectionDto) => (
            <div key={connection.id} className="figma_details-member">
              <UserAvatar
                isActive
                size="large"
                src={connection.user.picture.url || ''}
                userEmail={connection.user.email}
                userName={connection.user.name}
              />
              <div className="figma_details-member-text">
                <Text strong>
                  {connection.user.name || connection.user.email}
                </Text>
                <Text type="secondary">
                  {connection.user.email} · Figma:{' '}
                  {figmaAccountLabel(
                    connection.figmaAccount,
                    connection.user.email
                  )}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Connected{' '}
                  {moment(connection.connectedAt).format('MMM D, YYYY')}
                </Text>
              </div>
              <Popconfirm
                title={
                  <ConfirmText
                    title={`Revoke Figma access for ${connection.user.name}?`}
                    text="They can connect again from the plugin."
                  />
                }
                okText="Revoke access"
                // Outlined like the row's button: the app styles small
                // default buttons, not small or danger primary ones.
                okType="default"
                okButtonProps={{ danger: true }}
                onConfirm={() => revoke.mutateAsync(connection.id)}
              >
                <Button
                  danger
                  loading={
                    revoke.isLoading && revoke.variables === connection.id
                  }
                >
                  Revoke access
                </Button>
              </Popconfirm>
            </div>
          ))}
        </div>
        {revoke.isError && (
          <Text type="danger">
            Couldn&apos;t revoke access. Check your connection and try again.
          </Text>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Revoking access disconnects that member&apos;s Figma account from
          StreamWork. They&apos;re asked to connect again the next time they use
          the plugin.
        </Text>
      </section>
    </div>
  );
}
